import { splitHdnValue } from "@/helpers/hdnHelpers";
import { queries } from "@loanlink-nl/zero";
import type { HdnReceiverFeature } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { computed } from "vue";
import { useQuery } from "./useZero";

export type ProviderRecord = Row["hdnProvider"];
export type ProviderWithReceiver = ProviderRecord & {
	hdnReceiver?: {
		hdnReceiverFeature?: HdnReceiverFeature[];
	} | null;
};

type QueryRows<T> = readonly T[] | Record<string, T> | null | undefined;

function toRows<T>(rows: QueryRows<T>): T[] {
	if (!rows) {
		return [];
	}

	return Array.isArray(rows) ? [...rows] : Object.values(rows);
}

export function getProviderLabel(provider: ProviderRecord) {
	return provider.label ?? splitHdnValue(provider.name)[1];
}

export function providerSupportsInformatiebericht(
	provider?: Pick<ProviderWithReceiver, "hdnReceiver">,
) {
	const features = provider?.hdnReceiver?.hdnReceiverFeature ?? [];

	return features.some((feature) => {
		const featureName = feature.feature.toLowerCase().replace(/\s+/g, "");
		const featureValue = feature.value;

		return (
			featureName.includes("informatiebericht") &&
			(featureValue === true || featureValue === "true")
		);
	});
}

export function useProviders() {
	const { data: providers } = useQuery(queries.providers.all());
	const providerList = computed(() =>
		toRows<ProviderWithReceiver>(
			providers.value as QueryRows<ProviderWithReceiver>,
		),
	);
	const providersByCode = computed(() => {
		return new Map(
			providerList.value.map((provider) => [provider.name, provider]),
		);
	});

	function getProvider(value?: string) {
		if (!value) {
			return undefined;
		}

		return providersByCode.value.get(value);
	}

	function providerCodeSupportsInformatiebericht(value?: string) {
		return providerSupportsInformatiebericht(getProvider(value));
	}

	return {
		providerList,
		providersByCode,
		getProvider,
		providerCodeSupportsInformatiebericht,
	};
}
